import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { DEFAULTS } from '../constants';
import { useGetGLPostingProductCategoriesQuery } from '../../../services/Sublocation/SublocationService';
import { useGetCurrenciesQuery } from '../../../services/Currencies/CurrenciesService';
import { transformProductCategories } from '../utils/glPostingMethods';
import { readOpenPostingState, writeOpenPostingState } from '../utils/openPostingStateStorage';
import { useSublocationFetch } from './useSublocationFetch';
import { useDateConstraints } from './useDateConstraints';

const normalizeSublocationIds = (selectedSublocations = []) => {
  if (!Array.isArray(selectedSublocations)) return DEFAULTS.EMPTY_ARRAY;
  return selectedSublocations.map((id) => String(id ?? '').trim()).filter(Boolean);
};

const normalizeCurrencyIds = (selectedCurrencies = []) => {
  if (!Array.isArray(selectedCurrencies)) return DEFAULTS.EMPTY_ARRAY;

  return Array.from(
    new Set(
      selectedCurrencies
        .map((currencyId) =>
          String(currencyId ?? '')
            .trim()
            .replace(/\s+/g, ''),
        )
        .filter(Boolean),
    ),
  );
};

export const useGLPostingFilters = ({
  onResetState,
  onApplyFilters,
  skipProductTypeFetch = false,
  onApiError,
  persistOpenPostingState = true,
  restoreFromPersistedState = false,
}) => {
  const { data: productCategoriesData, isLoading: isLoadingProductTypes } = useGetGLPostingProductCategoriesQuery(
    undefined,
    { skip: skipProductTypeFetch },
  );
  const productTypes = useMemo(() => transformProductCategories(productCategoriesData), [productCategoriesData]);

  const persistedOpenPostingStateRef = useRef(
    persistOpenPostingState && restoreFromPersistedState ? readOpenPostingState() : {},
  );
  const persistedOpenPostingState = persistedOpenPostingStateRef.current;
  const persistedSelectedProductType = String(persistedOpenPostingState?.selectedProductType ?? DEFAULTS.EMPTY_STRING);
  const persistedSelectedX3ProductName = String(
    persistedOpenPostingState?.selectedX3ProductName ?? DEFAULTS.EMPTY_STRING,
  ).trim();

  const [selectedProductType, setSelectedProductType] = useState(
    () => persistedSelectedProductType || persistedSelectedX3ProductName,
  );
  const [selectedStartDate, setSelectedStartDate] = useState(() =>
    persistedOpenPostingState?.selectedStartDate ? String(persistedOpenPostingState.selectedStartDate) : null,
  );
  const [selectedEndDate, setSelectedEndDate] = useState(() =>
    persistedOpenPostingState?.selectedEndDate ? String(persistedOpenPostingState.selectedEndDate) : null,
  );
  const [selectedCurrencies, setSelectedCurrencies] = useState(DEFAULTS.EMPTY_ARRAY);
  const [selectedSublocations, setSelectedSublocations] = useState(() =>
    normalizeSublocationIds(persistedOpenPostingState?.selectedSublocations),
  );
  const [selectedPostingDate, setSelectedPostingDate] = useState(() =>
    persistedOpenPostingState?.selectedPostingDate ? String(persistedOpenPostingState.selectedPostingDate) : null,
  );

  const { currentData: currenciesData, isLoading: isLoadingCurrencies } = useGetCurrenciesQuery(undefined, {
    skip: !selectedProductType,
  });

  const currencies = useMemo(
    () =>
      (Array.isArray(currenciesData) ? currenciesData : [])
        .filter((currency) => currency?.isActive !== false)
        .map((currency) => {
          const id = String(currency?.id ?? '')
            .trim()
            .replace(/\s+/g, '');
          const code = String(currency?.abbreviation ?? '')
            .trim()
            .toUpperCase();
          const name = String(currency?.name ?? '').trim();
          if (!id || !code || !name) return null;

          return {
            id,
            code,
            name,
            label: `${name} (${code})`,
          };
        })
        .filter(Boolean),
    [currenciesData],
  );

  const selectedX3ProductName = useMemo(() => {
    if (!selectedProductType) return persistedSelectedX3ProductName || null;
    const selectedValue = String(selectedProductType).trim();
    if (!selectedValue) return persistedSelectedX3ProductName || null;

    const selectedById = productTypes.find((productType) => String(productType.id) === selectedValue);
    if (selectedById?.x3ProductName) return selectedById.x3ProductName;

    const selectedValueUpper = selectedValue.toUpperCase();
    const selectedByName = productTypes.find(
      (productType) =>
        productType.x3ProductName === selectedValueUpper || productType.name?.toUpperCase() === selectedValueUpper,
    );
    if (selectedByName?.x3ProductName) return selectedByName.x3ProductName;

    return persistedSelectedX3ProductName || null;
  }, [productTypes, selectedProductType, persistedSelectedX3ProductName]);

  useEffect(() => {
    const normalizedSelectedValue = String(selectedProductType ?? '').trim();
    if (!normalizedSelectedValue || productTypes.length === 0) return;

    const hasMatchingId = productTypes.some((productType) => String(productType?.id) === normalizedSelectedValue);
    if (hasMatchingId) return;

    const normalizedSelectedValueUpper = normalizedSelectedValue.toUpperCase();
    const matchedByName = productTypes.find(
      (productType) =>
        productType?.x3ProductName === normalizedSelectedValueUpper ||
        productType?.name?.toUpperCase() === normalizedSelectedValueUpper,
    );
    if (matchedByName?.id !== undefined && matchedByName?.id !== null) {
      setSelectedProductType(String(matchedByName.id));
    }
  }, [selectedProductType, productTypes]);

  useEffect(() => {
    if (!persistOpenPostingState) return;

    writeOpenPostingState({
      selectedProductType: selectedProductType || DEFAULTS.EMPTY_STRING,
      selectedX3ProductName: selectedX3ProductName || null,
      selectedStartDate: selectedStartDate || null,
      selectedEndDate: selectedEndDate || null,
      selectedSublocations: normalizeSublocationIds(selectedSublocations),
      selectedPostingDate: selectedPostingDate || null,
    });
  }, [
    selectedProductType,
    selectedX3ProductName,
    selectedStartDate,
    selectedEndDate,
    selectedSublocations,
    selectedPostingDate,
    persistOpenPostingState,
  ]);

  // Sublocation fetching, sorting, filtering
  const {
    apiSublocations,
    sortedSublocations,
    filteredSublocations,
    sublocationSearchTerm,
    isLoadingSublocations,
    hasSublocations,
    onSearch: handleSublocationSearch,
    resetSublocations,
  } = useSublocationFetch({ selectedX3ProductName, selectedCurrencies, onApiError });

  // Date constraint calculation and handlers
  const {
    todayDate,
    minSelectableStartDate,
    minSelectableEndDate,
    canEditDates,
    dateConstraintHint,
    dateConstraintWarnings,
    onStartDateChange: handleStartDateChange,
    onEndDateChange: handleEndDateChange,
    shouldDisableStartDate,
    shouldDisableEndDate,
    selectedSublocationIdSet,
  } = useDateConstraints({
    selectedSublocations,
    apiSublocations,
    selectedStartDate,
    selectedEndDate,
    setSelectedStartDate,
    setSelectedEndDate,
    onResetState,
  });

  const hasDateConstraintIssue = Boolean(dateConstraintHint) || dateConstraintWarnings.length > 0;
  const isStartDateDisabled = !selectedProductType || !hasSublocations || !canEditDates || hasDateConstraintIssue;
  const isEndDateDisabled =
    !selectedProductType || !hasSublocations || !canEditDates || hasDateConstraintIssue || !selectedStartDate;

  const resetDatesAndSublocations = useCallback(() => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectedSublocations(DEFAULTS.EMPTY_ARRAY);
    onResetState?.();
  }, [onResetState]);

  const resetFromDateDown = useCallback(() => {
    resetDatesAndSublocations();
    resetSublocations();
  }, [resetDatesAndSublocations, resetSublocations]);

  const handleProductTypeChange = useCallback(
    (event) => {
      setSelectedProductType(event?.target?.value ?? DEFAULTS.EMPTY_STRING);
      setSelectedCurrencies(DEFAULTS.EMPTY_ARRAY);
      resetFromDateDown();
    },
    [resetFromDateDown],
  );

  const handleCurrencyChange = useCallback(
    (newSelectedCurrencies) => {
      setSelectedCurrencies(normalizeCurrencyIds(newSelectedCurrencies));
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setSelectedSublocations(DEFAULTS.EMPTY_ARRAY);
      onResetState?.();
    },
    [onResetState],
  );

  const handleSublocationChange = useCallback(
    (newSelectedSublocations) => {
      setSelectedSublocations(normalizeSublocationIds(newSelectedSublocations));
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      onResetState?.();
    },
    [onResetState],
  );

  const resetSelections = useCallback(() => {
    setSelectedProductType(DEFAULTS.EMPTY_STRING);
    setSelectedCurrencies(DEFAULTS.EMPTY_ARRAY);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectedSublocations(DEFAULTS.EMPTY_ARRAY);
    setSelectedPostingDate(null);
    resetSublocations();
    onResetState?.();
  }, [onResetState, resetSublocations]);

  const handlePostingDateChange = useCallback((newDate) => {
    if (!newDate) {
      setSelectedPostingDate(null);
      return;
    }

    const parsed = dayjs(newDate).startOf('day');
    setSelectedPostingDate(parsed.isValid() ? parsed.format('YYYY-MM-DD') : null);
  }, []);

  const handleSelectAllSublocations = useCallback(() => {
    setSelectedSublocations(normalizeSublocationIds(filteredSublocations.map((sub) => sub?.id)));
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    onResetState?.();
  }, [filteredSublocations, onResetState]);

  const handleDeselectAllSublocations = useCallback(() => {
    setSelectedSublocations(DEFAULTS.EMPTY_ARRAY);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    onResetState?.();
  }, [onResetState]);

  const handleApplySublocations = useCallback(() => {
    const selectedSublocationDetails = apiSublocations
      .filter((sublocation) => selectedSublocationIdSet.has(String(sublocation?.id ?? '').trim()))
      .map((sublocation) => ({
        id: sublocation?.id,
        name: sublocation?.name ?? DEFAULTS.EMPTY_STRING,
      }));

    onApplyFilters?.({
      x3ProductName: selectedX3ProductName,
      currencyIds: selectedCurrencies,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      sublocationIds: Array.isArray(selectedSublocations) ? selectedSublocations : [],
      selectedSublocationDetails,
    });
  }, [
    onApplyFilters,
    selectedX3ProductName,
    selectedCurrencies,
    selectedStartDate,
    selectedEndDate,
    selectedSublocations,
    apiSublocations,
    selectedSublocationIdSet,
  ]);

  return {
    filters: {
      productTypes,
      selectedProductType,
      onProductTypeChange: handleProductTypeChange,
      isProductTypesLoading: isLoadingProductTypes,
      currencies,
      selectedCurrencies,
      onCurrencyChange: handleCurrencyChange,
      isCurrenciesLoading: isLoadingCurrencies,
      selectedStartDate,
      selectedEndDate,
      onStartDateChange: handleStartDateChange,
      onEndDateChange: handleEndDateChange,
      isStartDateDisabled,
      isEndDateDisabled,
      minStartDate: minSelectableStartDate,
      maxStartDate: todayDate,
      minEndDate: minSelectableEndDate,
      maxEndDate: todayDate,
      shouldDisableStartDate,
      shouldDisableEndDate,
      dateConstraintHint,
      dateConstraintWarnings,
      selectedProductId: selectedProductType || null,
      selectedX3ProductName,
      resetSelections,
      selectedPostingDate,
      onPostingDateChange: handlePostingDateChange,
    },
    sublocationState: {
      sublocations: filteredSublocations,
      allSublocations: sortedSublocations,
      selectedSublocations,
      searchTerm: sublocationSearchTerm,
      isLoading: isLoadingSublocations,
      onChange: handleSublocationChange,
      onSearch: handleSublocationSearch,
      onSelectAll: handleSelectAllSublocations,
      onDeselectAll: handleDeselectAllSublocations,
      onApply: handleApplySublocations,
    },
  };
};
